import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export const placeOrder = createAsyncThunk(
  'cart/placeOrder',
  async ({ userId, orderData }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'customers', userId);
      
      // Create order object with timestamp
      const order = {
        ...orderData,
        status: 'pending',
        createdAt: new Date().toISOString(),
        orderId: Math.random().toString(36).substr(2, 9), // Simple order ID generation
      };

      // Update user's orders in Firestore
      await updateDoc(userRef, {
        orders: arrayUnion(order)
      });

      return order;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching user's orders
export const fetchOrders = createAsyncThunk(
  'cart/fetchOrders',
  async (userId, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'customers', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      return userData.orders || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const calculateItemPrice = (item) => {
  let total = item.price * item.quantity;
  
  if (item.extras) {
    Object.values(item.extras).forEach(category => {
      total += category.reduce((sum, extra) => {
        // Only add price if it's greater than zero and quantity is greater than zero
        return sum + (extra.price > 0 && extra.quantity > 0 ? extra.price * extra.quantity : 0);
      }, 0) * item.quantity;
    });
  }
  return total;
};



const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [],
    totalQuantity: 0,
    totalPrice: 0,
    orders: [], // Add orders array to state
    orderStatus: 'idle', // For tracking order placement status
    orderError: null,
    loadingOrders: false
  },
  reducers: {
    addToCart: (state, action) => {
      const itemToAdd = action.payload;
      const existingItem = state.items.find(item =>
        item.name === itemToAdd.name &&
        item.type === itemToAdd.type &&
        JSON.stringify(item.extras) === JSON.stringify(itemToAdd.extras)
      );

      if (!existingItem) {
        state.items.push({
          ...itemToAdd,
          totalPrice: calculateItemPrice(itemToAdd)
        });
        state.totalQuantity += itemToAdd.quantity;
        state.totalPrice += calculateItemPrice(itemToAdd);
      } else {
        existingItem.quantity += itemToAdd.quantity;
        existingItem.totalPrice = calculateItemPrice(existingItem);
        state.totalQuantity += itemToAdd.quantity;
        state.totalPrice += calculateItemPrice(itemToAdd);
      }
    },
    removeFromCart: (state, action) => {
      const itemId = action.payload;
      const index = state.items.findIndex(item => item.id === itemId);
     
      if (index !== -1) {
        const item = state.items[index];
        state.totalQuantity -= item.quantity;
        state.totalPrice -= item.totalPrice;
        state.items.splice(index, 1);
      }
    },
    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.items.find(item => item.id === id);
     
      if (item) {
        const previousQuantity = item.quantity;
        item.quantity = quantity;
        item.totalPrice = calculateItemPrice(item);
       
        state.totalPrice = state.totalPrice - (previousQuantity * item.price) + (quantity * item.price);
        state.totalQuantity = state.totalQuantity - previousQuantity + quantity;
      }
    },
    updateExtraQuantity: (state, action) => {
      const { id, category, extraName, quantity } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === id);
    
      if (itemIndex !== -1) {
        const item = state.items[itemIndex];
    
        if (item.extras && Array.isArray(item.extras[category])) {
          // If quantity is 0, remove the extra entirely
          const updatedExtras = {
            ...item.extras,
            [category]: quantity === 0
              ? item.extras[category].filter(extra => extra.name !== extraName)
              : item.extras[category].map(extra =>
                  extra.name === extraName
                    ? { ...extra, quantity }
                    : extra
                )
          };
    
          // If category is empty after update, remove it
          if (updatedExtras[category].length === 0) {
            delete updatedExtras[category];
          }
    
          // Update item with new extras
          const updatedItem = {
            ...item,
            extras: updatedExtras
          };
    
          // Recalculate total price
          updatedItem.totalPrice = calculateItemPrice(updatedItem);
    
          // Update state
          state.items[itemIndex] = updatedItem;
    
          // Update total cart price
          state.totalPrice = state.items.reduce(
            (total, item) => total + item.totalPrice,
            0
          );
        }
      }
    },
    
    clearCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.totalPrice = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle placeOrder
      .addCase(placeOrder.pending, (state) => {
        state.orderStatus = 'loading';
        state.orderError = null;
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.orderStatus = 'succeeded';
        state.orders.push(action.payload);
        // Clear cart after successful order
        state.items = [];
        state.totalQuantity = 0;
        state.totalPrice = 0;
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.orderStatus = 'failed';
        state.orderError = action.payload;
      })
      // Handle fetchOrders
      .addCase(fetchOrders.pending, (state) => {
        state.loadingOrders = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loadingOrders = false;
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loadingOrders = false;
        state.orderError = action.payload;
      });
  },
});

export const { 
  addToCart, 
  removeFromCart, 
  updateQuantity, 
  updateExtraQuantity, 
  clearCart 
} = cartSlice.actions;

export default cartSlice.reducer;